import { Column, ColumnBodyOptions } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, {
  CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import globalCourseStore from '../../../../../../../../stores/global-course-store';
import { UserSummaryType } from '../../../../../../../../api/types/user/user-summary.type';
import { Button } from 'primereact/button';
import { HeaderAttendance } from './components/header-attendance';
import { RequestEnrollment } from '../../../../../../../../api/types/programs/course/exams/requests/request-exam-enrollment-update';
import {
  ExamSessionDetails,
  StudentSessionRegistration,
} from '../../../../../../../../api/types/programs/course/exams/response/exam-metadata';
import { ExamStudentEligibility } from '../../../../../../../../api/types/programs/course/exams/exam-sessions-enrollment-entity';
import globalProgramStore from '../../../../../../../../stores/program-store';
import { upsertStudentsEnrollmentOperations } from '../../componenets/students-enrollment/operations/upsert-students-enrollment-operations';
import {
  CheckBoxSeverity,
  ColorfulCheckbox,
} from '../../../../../../../../component/ui/colorful-checkbox/colorful-checkbox';
import { Tag } from 'primereact/tag';
import { UserSummeryAttendance } from '../../../../../../../../api/types/programs/course/course-details.type';
import { Checkbox } from 'primereact/checkbox';
import { toast } from 'react-toastify';

const columnStyle: CSSProperties = {
  textAlign: 'right',
  minWidth: '30px',
  maxWidth: '30px',
};

interface ExamPageStudentRegistrationsDatamodel {
  examId: number;
  examSession: ExamSessionDetails | null;
  setSessionDetails: (sessions: ExamSessionDetails[]) => any;
}

export const ExamPageStudentRegistrations: React.FC<
  ExamPageStudentRegistrationsDatamodel
> = ({ examId, examSession, setSessionDetails }) => {
  const currentCourse = globalCourseStore.getCurrentCourse();

  const [studentClassRegistrations, setStudentClassRegistrations] = useState<
    Record<number, RequestEnrollment>
  >({});

  const initialEnrollmentState = useRef<Record<number, RequestEnrollment>>({});

  const initializeStudentRegistrations = (
    examSession: ExamSessionDetails,
  ): Record<number, RequestEnrollment> => {
    const initialRegistrations: Record<number, RequestEnrollment> = {};

    examSession.studentsRegistrations.forEach(
      (registrationItem: StudentSessionRegistration) => {
        initialRegistrations[registrationItem.studentId] = {
          studentId: registrationItem.studentId,
          eligibility: registrationItem.examEligibility,
          enrollment: registrationItem.enrollment,
        };
      },
    );

    return initialRegistrations;
  };

  useEffect(() => {
    if (examSession) {
      const initialState = initializeStudentRegistrations(examSession);
      initialEnrollmentState.current = initialState; // Store for comparison
      setStudentClassRegistrations(initialState); // Update current state
    }
  }, [examSession]);

  const hasChanges = useMemo(() => {
    const initialKeys = Object.keys(initialEnrollmentState.current);
    const currentKeys = Object.keys(studentClassRegistrations);

    if (initialKeys.length !== currentKeys.length) return true;

    for (const key of currentKeys) {
      const studentId = Number(key);
      const initial = initialEnrollmentState.current[studentId];
      const current = studentClassRegistrations[studentId];

      if (!initial) return true;

      if (
        initial.eligibility !== current.eligibility ||
        initial.enrollment !== current.enrollment
      ) {
        return true;
      }
    }

    return false;
  }, [studentClassRegistrations]);

  const updateStudentEligibility = (
    studentId: number,
    eligibility: ExamStudentEligibility,
  ) => {
    if (eligibility === ExamStudentEligibility.NOT_ELIGIBILITY) {
      updateStudentEnrollment(studentId, false);
    }

    setStudentClassRegistrations((prev) => {
      const newRegistrations = { ...prev };

      if (!newRegistrations[studentId]) {
        newRegistrations[studentId] = {
          studentId,
          eligibility: eligibility,
        };
      } else {
        // Update existing registration
        newRegistrations[studentId] = {
          ...newRegistrations[studentId],
          eligibility: eligibility,
        };
      }

      return newRegistrations;
    });
  };

  const updateStudentEnrollment = (studentId: number, enrollment: boolean) => {
    if (
      enrollment &&
      studentClassRegistrations[studentId]?.eligibility ===
        ExamStudentEligibility.NOT_ELIGIBILITY
    ) {
      toast.warn('לא ניתן לרשום סטודנט שלא זכאי');
      return;
    }

    setStudentClassRegistrations((prev) => {
      const newRegistrations = { ...prev };

      if (!newRegistrations[studentId]) {
        newRegistrations[studentId] = {
          studentId,
          enrollment: enrollment,
          eligibility: ExamStudentEligibility.ELIGIBILITY,
        };
      } else {
        // Update existing registration
        newRegistrations[studentId] = {
          ...newRegistrations[studentId],
          enrollment: enrollment,
        };
      }

      return newRegistrations;
    });
  };

  const [loading, setLoading] = useState(false);

  const handleOnApprove = async () => {
    const currentProgramId = globalProgramStore.getCurrentId();

    await upsertStudentsEnrollmentOperations(
      currentProgramId,
      currentCourse?.metadata.id,
      examId,
      examSession?.id,
      initialEnrollmentState.current,
      studentClassRegistrations,
      setSessionDetails,
      setLoading,
    );
  };

  const getAttendanceElement = (
    attendedNumber: number,
    requiredAttendance: number,
  ) => {
    const attendancePercentage =
      requiredAttendance === 0 ? 1 : attendedNumber / requiredAttendance;
    return (
      <div style={{ display: 'flex', gap: '4px' }}>
        <label>
          {attendedNumber ?? 0}/{requiredAttendance ?? 0}
        </label>
        <Tag
          style={{ borderRadius: '50px' }}
          severity={
            attendancePercentage >= 0.8
              ? 'success'
              : attendancePercentage >= 0.6
                ? 'warning'
                : 'danger'
          }
          value={`${
            attendancePercentage ? Math.round(attendancePercentage * 100) : 0
          }%`}
        />
      </div>
    );
  };

  return (
    <div>
      <DataTable
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>}
        selectionMode={'multiple'}
        multiple={true}
        groupRowsBy="dsf"
        value={currentCourse?.details?.students}
        scrollable
        onSelectionChange={(e) => {
          //setSelectedUsers(e.value as UserSummaryType[]);
        }}
        dataKey="id"
        tableStyle={{
          direction: 'rtl',
          marginTop: '16px',
        }}
      >
        <Column
          expander={false}
          header="שם"
          field={'firstName'}
          style={columnStyle}
          body={(rowData: UserSummaryType) => (
            <label>
              {rowData.firstName} {rowData.lastName}
            </label>
          )}
        />

        <Column
          expander={false}
          header="ת.ז"
          field={'nationalId'}
          style={columnStyle}
        />

        <Column
          expander={false}
          header={() => (
            <HeaderAttendance
              headerVisibility={false}
              toolTip={'מספר ההרצאות בתוכנית שהיה משובץ חלקי הנוכחות'}
              text={'יחסית בקורס'}
            />
          )}
          field={'firstName'}
          style={{ width: '150px', textAlign: 'right' }}
          body={(
            rowData: UserSummeryAttendance,
            options: ColumnBodyOptions,
          ) => {
            console.log(
              `Course relation: ${rowData.courseAttendedLectures}  ${rowData.courseTotalRequiredLectures}`,
            );
            return getAttendanceElement(
              rowData.courseAttendedLectures,
              rowData.courseTotalRequiredLectures,
            );
          }}
        />

        <Column
          expander={false}
          header={() => (
            <HeaderAttendance
              headerVisibility={true}
              toolTip={'מספר ההרצאות בקורסים שהסתיימו שהיה משובץ חלקי הנוכחות'}
              text={'מוחלטת בקורס'}
            />
          )}
          field={'firstName'}
          style={{ width: '170px', textAlign: 'right' }}
          body={(
            rowData: UserSummeryAttendance,
            options: ColumnBodyOptions,
          ) => {
            const asd = globalCourseStore.getCurrentCourse()?.details?.lectures;
            console.log(
              `Course relation absolute : ${rowData.courseAttendedLectures}  ${
                globalCourseStore.getCurrentCourse()?.details?.lectures
                  .length ?? 0
              }  ${asd.length}`,
            );
            return getAttendanceElement(
              rowData.courseAttendedLectures,
              globalCourseStore.getCurrentCourse()?.details?.lectures.length ??
                0,
            );
          }}
        />

        <Column
          expander={false}
          header={() => (
            <HeaderAttendance
              headerVisibility={false}
              toolTip={'נוכחות בכל הקורסים באופן מצרפי (רק הקורסים שהסתיימו)'}
              text={'יחסית בתוכנית'}
            />
          )}
          field={'firstName'}
          style={{ width: '170px', textAlign: 'right' }}
          body={(
            rowData: UserSummeryAttendance,
            options: ColumnBodyOptions,
          ) => {
            console.log(
              `ProgramAbsolute: ${rowData.programAttendedLectures}   ${rowData.programTotalRequiredLectures}`,
            );
            return getAttendanceElement(
              rowData.programAttendedLectures,
              rowData.programTotalRequiredLectures,
            );
          }}
        />

        <Column
          expander={false}
          header={'זכאות'}
          field={'firstName'}
          style={columnStyle}
          body={(rowData: UserSummaryType) => (
            <Checkbox
              /*severity={CheckBoxSeverity.LIGHT_APPROVE}*/
              checked={
                studentClassRegistrations[rowData.id]?.eligibility ===
                ExamStudentEligibility.ELIGIBILITY
              }
              onChange={(e) =>
                updateStudentEligibility(
                  rowData.id,
                  ExamStudentEligibility.ELIGIBILITY,
                )
              }
            />
          )}
        />

        <Column
          expander={false}
          header={'מוחרג'}
          field={'firstName'}
          style={columnStyle}
          body={(rowData: UserSummaryType) => (
            <Checkbox
              /*severity={CheckBoxSeverity.WARNING}*/
              checked={
                studentClassRegistrations[rowData.id]?.eligibility ===
                ExamStudentEligibility.EXCLUDED
              }
              onChange={(e) =>
                updateStudentEligibility(
                  rowData.id,
                  ExamStudentEligibility.EXCLUDED,
                )
              }
            />
          )}
        />

        <Column
          expander={false}
          header={'לא זכאי'}
          field={'firstName'}
          style={columnStyle}
          body={(rowData: UserSummaryType) => (
            <Checkbox
              /*severity={CheckBoxSeverity.DANGER}*/
              checked={
                studentClassRegistrations[rowData.id]?.eligibility ===
                ExamStudentEligibility.NOT_ELIGIBILITY
              }
              onChange={(e) =>
                updateStudentEligibility(
                  rowData.id,
                  ExamStudentEligibility.NOT_ELIGIBILITY,
                )
              }
            />
          )}
        />

        <Column
          expander={false}
          header={''}
          field={'firstName'}
          style={columnStyle}
          body={(rowData: UserSummaryType) => <div />}
        />

        <Column
          expander={false}
          header={'רשום'}
          field={'firstName'}
          style={columnStyle}
          body={(rowData: UserSummaryType) => (
            <ColorfulCheckbox
              severity={CheckBoxSeverity.SUCCESS}
              checked={
                studentClassRegistrations[rowData.id]?.enrollment ?? false
              }
              onChange={(checked) =>
                updateStudentEnrollment(rowData.id, checked)
              }
            />
          )}
        />
      </DataTable>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '16px',
        }}
      >
        <Button
          style={{ height: '30px' }}
          label={'אישור'}
          loading={loading}
          disabled={!hasChanges}
          tooltip={hasChanges ? 'לחץ לאישור' : 'יש לבצע שינוי ברישום'}
          tooltipOptions={{ showOnDisabled: true }}
          onClick={handleOnApprove}
        />
      </div>
    </div>
  );
};
